import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from 'react-leaflet'
import L from 'leaflet'
import { ChevronLeft, ChevronRight, Download, FileText, Flame, Images, Loader2, RefreshCw, X } from 'lucide-react'
import { typLabel } from './Chips'
import { statusLabel } from './StatusBadge'
import { api } from '@pilot-hub/api-client'
import { useModal } from '@pilot-hub/ui/hooks/useModal'
import { HOTSPOT_EINORDNUNGEN, HOTSPOT_NICHT_MEHR_ERKANNT, hotspotEinordnungFarbe, hotspotEinordnungLabel } from '@pilot-hub/ui/components/einsatz/bericht'
import { PanoramaDialog } from '@pilot-hub/ui/components/karte/Panorama'
import 'leaflet/dist/leaflet.css'

/*
 * Admin-lokale Kopie der Medien-Galerie (packages/ui bleibt unverändert; Änderungen D5: Kartenleiste einklappbar, Raster füllt Breite,
 * 1-Satz-Hinweis, einheitliches Datum, Aufnahmeart-Chips mit Zähler).
 * Medien-Galerie: Fotos aus DJI Pilot 2 (Medien-Worker) je Einsatz – auch abgeschlossene – und Fotos ohne Einsatz.
 * Bilder werden nur mit Anmeldung geliefert (Blob-Abruf mit Token). Originale werden nach der Aufbewahrungsfrist gelöscht;
 * verkleinerte Fassung (2560 px) und Vorschau (400 px) bleiben. Download: Original, falls vorhanden, sonst verkleinerte Fassung.
 * Endpunkte (admin-api und fuehrung-api identisch): /dji/medien/galerie/{einsaetze,fotos,zip}, /dji/medien/datei/{id}/{art}.
 */

interface Hotspot { id: number; temp_max: number; temp_mittel: number | null; pixel_anzahl: number | null; bild_x: number | null; bild_y: number | null
  einordnung?: string | null; einordnung_am?: string | null; einordnung_von_name?: string | null
  /** Eingeordneter Hotspot, der bei einer Neuberechnung nicht mehr entstand (bleibt erhalten) */
  nicht_mehr_erkannt?: boolean; nicht_mehr_erkannt_am?: string | null; lat?: number | null; lon?: number | null }
interface Foto {
  id: number; name: string; typ: 'weit' | 'zoom' | 'ir' | 'pano' | null
  flug_id: number | null; flug_nr: string | null; drohne: string | null
  aufnahme_zeit: string | null; hochgeladen_am: string | null
  lat: number | null; lon: number | null; hoehe_rel_m: number | null; gimbal_nick: number | null; gimbal_gier: number | null
  temp_min: number | null; temp_max: number | null; ir_hinweis: string | null; breite_px: number | null; hoehe_px: number | null
  groesse_original: number | null; groesse_verkleinert: number | null
  panorama_art?: 'kugel' | 'breit' | 'vertikal' | null; pano_fov_h?: number | null; pano_fov_v?: number | null
  hat_vorschau: boolean; hat_verkleinert: boolean; hat_original: boolean; dateien_geloescht: boolean
  hotspots: Hotspot[]
  /** Für den Einsatzbericht markiert (nur Führung/Admin setzen das) */
  bericht_markiert?: boolean; bericht_markiert_am?: string | null; bericht_markiert_von_name?: string | null
}
interface EinsatzGruppe { id: number; titel: string; typ: string | null; status: string | null; datum: string | null; fotos: number; ir: number; hotspots: number }
interface Einsaetze { einsaetze: EinsatzGruppe[]; ohne_einsatz: { fotos: number; ir: number } }
type Filter = 'alle' | 'weit' | 'zoom' | 'ir' | 'pano' | 'hotspot' | 'bericht'

const TYP_TEXT: Record<string, string> = { weit: 'Weitwinkel', zoom: 'Zoom', ir: 'IR', pano: 'Panorama' }
const datum = (z: string | null | undefined) => z ? new Date(z).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'
const zeit = (z: string | null | undefined) => z ? new Date(z).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–'
const fehlerDetail = (e: any, fallback: string) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : fallback }
const aufnahme = (f: Foto) => f.aufnahme_zeit ?? f.hochgeladen_am
const mb = (b: number | null | undefined) => b ? `${(b / 1048576).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB` : '–'
const typFarbe = (f: Foto) => f.typ === 'ir' ? '#f97316' : f.typ === 'pano' ? '#8b5cf6' : '#2563eb'
// Abzeichen/Text in der Oberfläche: folgt dem Farbschema (im Hellen dunkleres Orange/Violett/Blau, Kontrast >= 4,5:1); die Karten-Marker bleiben bei typFarbe
const typFarbeUi = (f: Foto) => f.typ === 'ir' ? 'rgb(var(--ui-c-orange-500))' : f.typ === 'pano' ? 'rgb(var(--ui-c-violet-500))' : 'rgb(var(--ui-c-blue-600))'
const GRUNDKARTEN = {
  karte: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap', maxNativeZoom: 19 },
  satellit: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Luftbild: &copy; Esri, Maxar', maxNativeZoom: 19 },
}

// --- Bilder mit Anmeldung laden ---------------------------------------------------------------------------------------
// Höchstens 4 Bildabrufe gleichzeitig: 172 Vorschauen dürfen den Server nicht auf einmal fluten
const MAX_PARALLEL = 4
let laufend = 0
const warteschlange: (() => void)[] = []
function slotFreigeben() { laufend--; const n = warteschlange.shift(); if (n) n() }
function mitSlot<T>(job: () => Promise<T>, abgebrochen: () => boolean): Promise<T> {
  return new Promise<T>((ok, nein) => {
    const start = () => {
      if (abgebrochen()) { nein(new Error('abgebrochen')); return }
      laufend++
      job().then(v => { slotFreigeben(); ok(v) }, e => { slotFreigeben(); nein(e) })
    }
    if (laufend < MAX_PARALLEL) start()
    else warteschlange.push(start)
  })
}

function useBlobUrl(pfad: string | null, aktiv = true) {
  const [url, setUrl] = useState<string | null>(null)
  const [fehler, setFehler] = useState(false)
  const [versuch, setVersuch] = useState(0)
  useEffect(() => {
    setUrl(null); setFehler(false)
    if (!pfad || !aktiv) return
    let lebt = true
    let objekt: string | null = null
    // Bis zu 3 Versuche (Server kurz überlastet / Zeitüberschreitung); 404 = wirklich nicht (mehr) vorhanden, kein Wiederholen
    const laden = async () => {
      for (let i = 0; i < 3; i++) {
        try { return (await mitSlot(() => api.get(pfad, { responseType: 'blob', timeout: 30000 }), () => !lebt)).data as Blob }
        catch (e: any) {
          if (!lebt || e?.response?.status === 404) throw e
          await new Promise(r => setTimeout(r, 1000 * (i + 1)))
        }
      }
      throw new Error('Laden fehlgeschlagen')
    }
    laden()
      .then(blob => { objekt = URL.createObjectURL(blob); if (lebt) setUrl(objekt); else URL.revokeObjectURL(objekt) })
      .catch(() => { if (lebt) setFehler(true) })
    return () => { lebt = false; if (objekt) URL.revokeObjectURL(objekt) }
  }, [pfad, aktiv, versuch])
  return { url, fehler, nochmal: () => setVersuch(v => v + 1) }
}

/** Vorschaubild; wird erst geladen, wenn die Kachel in die Nähe des sichtbaren Bereichs kommt (lazy) */
function Vorschau({ foto }: { foto: Foto }) {
  const ref = useRef<HTMLDivElement>(null)
  const [sichtbar, setSichtbar] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setSichtbar(true); return }
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { setSichtbar(true); io.disconnect() } }, { rootMargin: '300px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  const { url, fehler, nochmal } = useBlobUrl(foto.hat_vorschau ? `/dji/medien/datei/${foto.id}/vorschau` : null, sichtbar)
  return (
    <div ref={ref} className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-dark-bg-tertiary">
      {url ? <img src={url} alt={foto.name} className="h-full w-full object-cover" draggable={false} />
        : (foto.hat_vorschau && fehler && !foto.dateien_geloescht)
          ? <button type="button" onClick={nochmal} className="px-2 text-center text-xs text-dark-text-muted underline">Laden fehlgeschlagen – erneut versuchen</button>
        : (!foto.hat_vorschau || fehler) ? <span className="px-2 text-center text-xs text-dark-text-muted">{foto.dateien_geloescht ? 'Bild nicht mehr vorhanden' : 'Keine Vorschau'}</span>
        : <div className="h-full w-full animate-pulse bg-dark-bg-tertiary" />}
    </div>
  )
}

// --- Karte ----------------------------------------------------------------------------------------------------------------
function Ausschnitt({ punkte }: { punkte: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    if (!punkte.length) return
    map.invalidateSize()
    if (punkte.length === 1) map.setView(punkte[0], 18)
    else map.fitBounds(L.latLngBounds(punkte), { padding: [24, 24], maxZoom: 19 })
  }, [map, punkte])
  return null
}

function Karte({ fotos, gewaehlt, onWahl }: { fotos: Foto[]; gewaehlt: number | null; onWahl: (id: number) => void }) {
  const [grund, setGrund] = useState<keyof typeof GRUNDKARTEN>('karte')
  const mitPos = useMemo(() => fotos.filter(f => f.lat != null && f.lon != null), [fotos])
  const punkte = useMemo(() => mitPos.map(f => [f.lat!, f.lon!] as [number, number]), [mitPos])
  const gk = GRUNDKARTEN[grund]
  if (!mitPos.length) return <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl border border-dark-border p-4 text-center text-sm text-dark-text-muted">Keine Aufnahmepositionen vorhanden.</div>
  return (
    <div className="relative z-0 h-full min-h-[240px] overflow-hidden rounded-xl border border-dark-border">
      <MapContainer center={punkte[0]} zoom={16} className="h-full w-full" scrollWheelZoom>
        <TileLayer key={grund} url={gk.url} attribution={gk.attribution} maxNativeZoom={gk.maxNativeZoom} maxZoom={20} />
        <Ausschnitt punkte={punkte} />
        {mitPos.filter(f => f.id !== gewaehlt).map(f => (
          <CircleMarker key={f.id} center={[f.lat!, f.lon!]} radius={8}
            pathOptions={{ color: f.bericht_markiert ? '#22c55e' : '#fff', weight: f.bericht_markiert ? 4 : 2, fillColor: typFarbe(f), fillOpacity: 0.9 }} eventHandlers={{ click: () => onWahl(f.id) }}>
            <Tooltip direction="top" offset={[0, -8]}>{TYP_TEXT[f.typ ?? 'weit']} · {zeit(aufnahme(f))}</Tooltip>
          </CircleMarker>
        ))}
        {mitPos.filter(f => f.id === gewaehlt).map(f => (
          <CircleMarker key={`g${f.id}`} center={[f.lat!, f.lon!]} radius={13}
            pathOptions={{ color: '#facc15', weight: 4, fillColor: typFarbe(f), fillOpacity: 1 }} eventHandlers={{ click: () => onWahl(f.id) }}>
            <Tooltip direction="top" offset={[0, -12]} permanent>{TYP_TEXT[f.typ ?? 'weit']} · {zeit(aufnahme(f))}</Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
      <div className="absolute right-2 top-2 z-[500] flex rounded-lg border border-dark-border bg-dark-bg-secondary p-0.5" role="group" aria-label="Hintergrundkarte">
        {(['karte', 'satellit'] as const).map(g => (
          <button key={g} type="button" aria-pressed={grund === g} onClick={() => setGrund(g)}
            className={`min-h-[32px] rounded-md px-2.5 text-xs font-medium ${grund === g ? 'bg-primary-600 text-white' : 'text-dark-text-muted hover:text-dark-text'}`}>{g === 'karte' ? 'Karte' : 'Satellit'}</button>
        ))}
      </div>
    </div>
  )
}

// --- Download ---------------------------------------------------------------------------------------------------------------
function speichern(blob: Blob, name: string) {
  const u = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = u; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(u), 10000)
}
const mitSuffix = (name: string, suffix: string) => { const i = name.lastIndexOf('.'); return i > 0 ? `${name.slice(0, i)}${suffix}${name.slice(i)}` : `${name}${suffix}.jpg` }

// --- Großansicht -----------------------------------------------------------------------------------------------------------
function Grossansicht({ fotos, index, onIndex, onClose, onMarkieren, onEinordnen }: {
  fotos: Foto[]; index: number; onIndex: (i: number) => void; onClose: () => void
  onMarkieren: (f: Foto) => Promise<string | null>; onEinordnen: (f: Foto, h: Hotspot, einordnung: string | null) => Promise<string | null>
}) {
  const f = fotos[index]
  const wurzel = useRef<HTMLDivElement>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const [meldung, setMeldung] = useState('')
  const [pano, setPano] = useState(false)
  const [busy, setBusy] = useState(false)
  useModal(true, wurzel, onClose, 'button[data-schliessen]')
  const vor = useCallback(() => onIndex(Math.min(fotos.length - 1, index + 1)), [index, fotos.length, onIndex])
  const zurueck = useCallback(() => onIndex(Math.max(0, index - 1)), [index, onIndex])
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (pano) return
      if (e.key === 'ArrowRight') { e.preventDefault(); vor() }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); zurueck() }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [vor, zurueck, pano])
  useEffect(() => { setMeldung('') }, [f.id])
  const { url, fehler, nochmal } = useBlobUrl(f.hat_verkleinert ? `/dji/medien/datei/${f.id}/verkleinert` : f.hat_vorschau ? `/dji/medien/datei/${f.id}/vorschau` : null)

  const herunterladen = async () => {
    setMeldung('')
    try {
      if (f.hat_original) {
        try { speichern((await api.get(`/dji/medien/datei/${f.id}/original`, { responseType: 'blob' })).data, f.name); return } catch { /* Original weg: verkleinerte Fassung */ }
      }
      speichern((await api.get(`/dji/medien/datei/${f.id}/verkleinert`, { responseType: 'blob' })).data, mitSuffix(f.name, '_verkleinert'))
      setMeldung('Das Original ist nach der Aufbewahrungsfrist gelöscht – heruntergeladen wurde die verkleinerte Fassung.')
    } catch { setMeldung('Download nicht möglich: Datei nicht (mehr) vorhanden.') }
  }

  const hs = f.typ === 'ir' && f.breite_px && f.hoehe_px ? f.hotspots.filter(h => h.bild_x != null && h.bild_y != null) : []
  const zeile = (k: string, v: React.ReactNode) => v == null || v === '' ? null : <div className="flex justify-between gap-3 border-b border-dark-border/60 py-1.5 text-sm"><dt className="text-dark-text-muted">{k}</dt><dd className="text-right text-dark-text">{v}</dd></div>

  return createPortal(
    <div ref={wurzel} role="dialog" aria-modal="true" aria-label={`Foto ${f.name}`} tabIndex={-1}
      className="fixed inset-0 z-[3000] flex flex-col ui-dunkel bg-black text-dark-text lg:flex-row">
      <div className="relative flex min-h-0 flex-1 items-center justify-center"
        onTouchStart={e => { start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY } }}
        onTouchEnd={e => {
          const s = start.current; start.current = null
          if (!s) return
          const dx = e.changedTouches[0].clientX - s.x, dy = e.changedTouches[0].clientY - s.y
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) { dx < 0 ? vor() : zurueck() }
        }}>
        {url ? (
          <div className="relative max-h-full max-w-full">
            <img src={url} alt={f.name} className="block max-h-[calc(100dvh-14rem)] max-w-full object-contain lg:max-h-[100dvh]" draggable={false} />
            {hs.map(h => (
              <span key={h.id} title={`Hotspot ${h.temp_max.toFixed(1)} °C${h.nicht_mehr_erkannt ? ' (bei Neuberechnung nicht mehr erkannt)' : ''}`} className={`pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-red-500/20 ${h.nicht_mehr_erkannt ? 'border-dashed border-amber-400' : 'border-red-500'}`}
                style={{ left: `${(h.bild_x! / f.breite_px!) * 100}%`, top: `${(h.bild_y! / f.hoehe_px!) * 100}%` }} />
            ))}
          </div>
        ) : fehler || (!f.hat_verkleinert && !f.hat_vorschau)
          ? <p className="px-6 text-center text-sm text-dark-text-muted">{f.dateien_geloescht ? 'Bild nicht mehr vorhanden.' : (f.hat_verkleinert || f.hat_vorschau) ? <>Das Bild konnte gerade nicht geladen werden. <button type="button" onClick={nochmal} className="underline">Erneut versuchen</button></> : 'Für dieses Foto ist keine Bilddatei vorhanden.'}{f.dateien_geloescht && f.bericht_markiert ? ' Die Markierung für den Bericht bleibt erhalten.' : ''}</p>
          : <Loader2 className="h-8 w-8 animate-spin text-dark-text-muted" aria-label="Wird geladen" />}
        <button type="button" onClick={zurueck} disabled={index === 0} aria-label="Vorheriges Foto"
          className="absolute left-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white disabled:opacity-30"><ChevronLeft className="h-6 w-6" /></button>
        <button type="button" onClick={vor} disabled={index === fotos.length - 1} aria-label="Nächstes Foto"
          className="absolute right-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white disabled:opacity-30"><ChevronRight className="h-6 w-6" /></button>
        <div className="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs text-white">{index + 1} / {fotos.length}</div>
      </div>

      <aside className="flex max-h-[45dvh] shrink-0 flex-col overflow-y-auto border-t border-dark-border bg-dark-bg-secondary p-4 lg:max-h-none lg:w-[340px] lg:border-l lg:border-t-0">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs font-semibold" style={{ color: typFarbeUi(f) }}>{TYP_TEXT[f.typ ?? 'weit']}{f.panorama_art ? ` · ${f.panorama_art}` : ''}</div>
            <div className="truncate text-sm font-semibold text-dark-text" title={f.name}>{f.name}</div>
          </div>
          <button type="button" data-schliessen onClick={onClose} aria-label="Schließen"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-dark-border text-dark-text"><X className="h-5 w-5" /></button>
        </div>
        <dl>
          {zeile('Aufnahme', zeit(aufnahme(f)))}
          {zeile('Koordinate', f.lat != null && f.lon != null ? `${f.lat.toFixed(6)}, ${f.lon.toFixed(6)}` : null)}
          {zeile('Höhe über Start', f.hoehe_rel_m != null ? `${Math.round(f.hoehe_rel_m)} m` : null)}
          {zeile('Blickrichtung', f.gimbal_gier != null ? `${Math.round(((f.gimbal_gier % 360) + 360) % 360)}°` : null)}
          {zeile('Neigung', f.gimbal_nick != null ? `${Math.round(f.gimbal_nick)}°` : null)}
          {zeile('Flug', f.flug_nr ?? (f.flug_id ? `#${f.flug_id}` : null))}
          {zeile('Drohne', f.drohne)}
          {zeile('Bildgröße', f.breite_px && f.hoehe_px ? `${f.breite_px} × ${f.hoehe_px} px` : null)}
          {zeile('Dateigröße', f.hat_original ? `Original ${mb(f.groesse_original)}` : `verkleinert ${mb(f.groesse_verkleinert)}`)}
          {f.typ === 'ir' && zeile('Temperatur', f.temp_max != null ? `${f.temp_min?.toFixed(1) ?? '–'} bis ${f.temp_max.toFixed(1)} °C` : null)}
        </dl>
        <div className="mt-4 rounded-lg border border-dark-border p-3" data-testid="bericht-bereich">
          <div className="flex items-center gap-2 text-sm font-semibold text-dark-text"><FileText className="h-4 w-4 text-green-400" />Einsatzbericht</div>
          <p className="mt-1 text-xs text-dark-text-muted" data-testid="bericht-status">{f.bericht_markiert ? `Für den Bericht markiert${f.bericht_markiert_von_name ? ` von ${f.bericht_markiert_von_name}` : ''}${f.bericht_markiert_am ? `, ${zeit(f.bericht_markiert_am)}` : ''}.` : 'Nicht für den Bericht markiert.'}</p>
          <button type="button" disabled={busy} aria-pressed={!!f.bericht_markiert} data-testid="bericht-umschalten"
            onClick={async () => { setBusy(true); setMeldung(await onMarkieren(f) ?? ''); setBusy(false) }}
            className={`mt-2 inline-flex min-h-[48px] items-center gap-2 rounded-lg border px-4 text-sm font-semibold disabled:opacity-50 ${f.bericht_markiert ? 'border-green-500 bg-green-500/15 text-dark-text' : 'border-dark-border text-dark-text'}`}>
            <FileText className="h-4 w-4" />{f.bericht_markiert ? 'Markierung entfernen' : 'Für den Bericht markieren'}</button>
        </div>
        {f.typ === 'ir' && f.temp_max == null && f.ir_hinweis && <p className="mt-2 text-sm text-amber-400">{f.ir_hinweis}</p>}
        {f.typ === 'ir' && f.hotspots.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-dark-text"><Flame className="h-4 w-4 text-red-400" />Hotspots ({f.hotspots.length})</div>
            <ul className="space-y-1 text-sm text-dark-text-muted">
              {f.hotspots.map(h => (
                <li key={h.id} className="space-y-1" data-testid={`hotspot-${h.id}`}>
                  <div>{h.temp_max.toFixed(1)} °C{h.temp_mittel != null && <> (Ø {h.temp_mittel.toFixed(1)} °C)</>}{h.pixel_anzahl != null && <> · {h.pixel_anzahl} Bildpunkte</>}
                    {' · '}<span className="font-semibold" style={{ color: hotspotEinordnungFarbe(h.einordnung) ?? undefined }}>{hotspotEinordnungLabel(h.einordnung)}</span></div>
                  <div className="flex flex-wrap gap-1" role="group" aria-label="Hotspot einordnen">
                    {HOTSPOT_EINORDNUNGEN.map(x => (
                      <button key={x.key} type="button" disabled={busy} aria-pressed={h.einordnung === x.key}
                        onClick={async () => { setBusy(true); setMeldung(await onEinordnen(f, h, h.einordnung === x.key ? null : x.key) ?? ''); setBusy(false) }}
                        className={`min-h-[44px] rounded-lg border px-3 text-xs font-semibold disabled:opacity-50 ${h.einordnung === x.key ? 'border-primary-500 bg-primary-500/20 text-dark-text' : 'border-dark-border text-dark-text-muted'}`}>{x.label}</button>
                    ))}
                  </div>
                  {h.nicht_mehr_erkannt && <div className="text-xs font-semibold text-amber-400" data-testid="hotspot-nicht-mehr-erkannt">{HOTSPOT_NICHT_MEHR_ERKANNT}</div>}
                  {h.einordnung && <div className="text-xs">{h.einordnung_von_name ?? 'Unbekannt'} · {zeit(h.einordnung_am)}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!f.hat_original && f.hat_verkleinert && <p className="mt-3 rounded-lg bg-dark-bg-tertiary px-3 py-2 text-xs text-dark-text-muted">Original gelöscht (Aufbewahrungsfrist) – es steht die verkleinerte Fassung zur Verfügung.</p>}
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={herunterladen} disabled={!f.hat_original && !f.hat_verkleinert}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-primary-500 bg-primary-500/15 px-4 text-sm font-semibold text-dark-text disabled:opacity-40">
            <Download className="h-4 w-4" />{f.hat_original ? 'Original laden' : 'Verkleinert laden'}</button>
          {f.typ === 'pano' && (f.hat_verkleinert || f.hat_vorschau) && (
            <button type="button" onClick={() => setPano(true)} className="min-h-[44px] rounded-lg border border-dark-border px-4 text-sm font-semibold text-dark-text">Panorama öffnen</button>)}
        </div>
        {meldung && <p role="status" className="mt-2 text-xs text-amber-400">{meldung}</p>}
      </aside>
      {pano && <PanoramaDialog pano={{ ...f, hat_pano_gross: f.typ === 'pano', hat_pano_klein: f.typ === 'pano' }} onClose={() => setPano(false)} />}
    </div>,
    document.body,
  )
}

// --- Seite --------------------------------------------------------------------------------------------------------------------
export function MedienGalerie() {
  const [params, setParams] = useSearchParams()
  const [gruppen, setGruppen] = useState<Einsaetze | null>(null)
  const [fotos, setFotos] = useState<Foto[] | null>(null)
  const [fehler, setFehler] = useState('')
  const [filter, setFilter] = useState<Filter>('alle')
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  const [offen, setOffen] = useState<number | null>(null)     // Index in sichtbar
  const [zipLaeuft, setZipLaeuft] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState('')
  const [kartePlatz, setKartePlatz] = useState(true)   // Karte ein-/ausklappbar
  const kacheln = useRef<Record<number, HTMLButtonElement | null>>({})

  const einsatzParam = params.get('gruppe')           // Einsatz-ID oder „ohne“ (nicht „einsatz“: das ist in Führung die globale Einsatzwahl)
  const flugParam = params.get('flug')
  const setParam = (k: string, v: string | null) => setParams(p => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n }, { replace: true })

  const gruppenLaden = useCallback(async () => {
    try { setGruppen((await api.get('/dji/medien/galerie/einsaetze')).data as Einsaetze); setFehler('') }
    catch { setFehler('Einsatzliste konnte nicht geladen werden.') }
  }, [])
  useEffect(() => { void gruppenLaden() }, [gruppenLaden])

  // Auswahl: gewünschter Einsatz, sonst der neueste mit Fotos
  const auswahl = useMemo(() => {
    if (!gruppen) return null
    if (einsatzParam === 'ohne' && gruppen.ohne_einsatz.fotos > 0) return 'ohne'
    if (einsatzParam && gruppen.einsaetze.some(e => String(e.id) === einsatzParam)) return einsatzParam
    return gruppen.einsaetze[0] ? String(gruppen.einsaetze[0].id) : gruppen.ohne_einsatz.fotos > 0 ? 'ohne' : null
  }, [gruppen, einsatzParam])

  const fotosLaden = useCallback(async () => {
    if (!auswahl) { setFotos([]); return }
    try {
      const r = await api.get('/dji/medien/galerie/fotos', { params: auswahl === 'ohne' ? { ohne_einsatz: true } : { einsatz_id: auswahl } })
      setFotos(r.data as Foto[]); setFehler('')
    } catch { setFotos([]); setFehler('Fotos konnten nicht geladen werden.') }
  }, [auswahl])
  useEffect(() => { setFotos(null); setGewaehlt(null); setOffen(null); setFilter('alle'); void fotosLaden() }, [fotosLaden])

  const fluege = useMemo(() => {
    const m = new Map<number, string>()
    ;(fotos ?? []).forEach(f => { if (f.flug_id != null && !m.has(f.flug_id)) m.set(f.flug_id, [f.flug_nr ?? `#${f.flug_id}`, f.drohne].filter(Boolean).join(' · ')) })
    return [...m.entries()]
  }, [fotos])
  const flug = flugParam === 'ohne' ? 'ohne' : flugParam && fluege.some(([id]) => String(id) === flugParam) ? flugParam : ''

  const imFlug = useMemo(() => (fotos ?? []).filter(f => !flug || (flug === 'ohne' ? f.flug_id == null : String(f.flug_id) === flug)), [fotos, flug])
  const zaehler = useMemo(() => ({
    alle: imFlug.length, weit: imFlug.filter(f => f.typ === 'weit').length, zoom: imFlug.filter(f => f.typ === 'zoom').length,
    ir: imFlug.filter(f => f.typ === 'ir').length, pano: imFlug.filter(f => f.typ === 'pano').length, hotspot: imFlug.filter(f => f.hotspots.length > 0).length,
    bericht: imFlug.filter(f => f.bericht_markiert).length,
  }), [imFlug])
  const sichtbar = useMemo(() => imFlug.filter(f => filter === 'alle' || (filter === 'hotspot' ? f.hotspots.length > 0 : filter === 'bericht' ? !!f.bericht_markiert : f.typ === filter)), [imFlug, filter])
  useEffect(() => { if (filter !== 'alle' && zaehler[filter] === 0) setFilter('alle') }, [filter, zaehler])

  // Markieren für den Einsatzbericht / Hotspot einordnen (Führung und Admin; die Rollenprüfung macht das Backend)
  const markieren = useCallback(async (f: Foto): Promise<string | null> => {
    try {
      const r = await api.put(`/dji/medien/galerie/foto/${f.id}/bericht`, { markiert: !f.bericht_markiert })
      setFotos(alt => (alt ?? []).map(x => x.id === f.id ? { ...x, bericht_markiert: !!r.data.bericht_markiert, bericht_markiert_am: r.data.bericht_markiert_am ?? null, bericht_markiert_von_name: r.data.bericht_markiert_von_name ?? null } : x))
      return null
    } catch (e) { return fehlerDetail(e, 'Die Markierung konnte nicht gespeichert werden.') }
  }, [])
  const einordnen = useCallback(async (f: Foto, h: Hotspot, einordnung: string | null): Promise<string | null> => {
    try {
      const r = await api.put(`/dji/medien/galerie/hotspot/${h.id}/einordnung`, { einordnung })
      setFotos(alt => (alt ?? []).map(x => x.id !== f.id ? x : { ...x, hotspots: x.hotspots.map(y => y.id !== h.id ? y : { ...y, einordnung: r.data.einordnung ?? null, einordnung_am: r.data.einordnung_am ?? null, einordnung_von_name: null }) }))
      void fotosLaden()   // Name der Person nachladen
      return null
    } catch (e) { return fehlerDetail(e, 'Die Einordnung konnte nicht gespeichert werden.') }
  }, [fotosLaden])
  const [kachelFehler, setKachelFehler] = useState('')

  const waehlen = useCallback((id: number) => {
    setGewaehlt(id)
    kacheln.current[id]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [])

  const gruppe = auswahl && auswahl !== 'ohne' ? gruppen?.einsaetze.find(e => String(e.id) === auswahl) : null

  const zipLaden = async () => {
    if (!sichtbar.length) return
    setHinweis(''); setZipLaeuft('Wird erstellt …')
    try {
      const r = await api.get('/dji/medien/galerie/zip', {
        params: { ids: sichtbar.map(f => f.id).join(',') }, responseType: 'blob', timeout: 0,
        onDownloadProgress: e => setZipLaeuft(`Lädt … ${mb(e.loaded)}`),
      })
      speichern(r.data as Blob, `fotos_${auswahl === 'ohne' ? 'ohne-einsatz' : `einsatz-${auswahl}`}.zip`)
      const ohneOriginal = sichtbar.filter(f => !f.hat_original).length
      if (ohneOriginal) setHinweis(`${ohneOriginal} Foto(s) ohne Original (Aufbewahrungsfrist): im ZIP ist die verkleinerte Fassung enthalten (siehe HINWEIS.txt).`)
    } catch { setHinweis('ZIP konnte nicht erstellt werden.') }
    finally { setZipLaeuft(null) }
  }

  const chip = (k: Filter, text: string) => (k === 'alle' || zaehler[k] > 0) && (
    <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}
      className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-3.5 text-sm ${filter === k ? 'border-primary-500/60 bg-primary-500/15 text-dark-text' : 'border-dark-border bg-dark-bg-tertiary/30 text-dark-text-muted hover:text-dark-text'}`}>
      {(k === 'weit' || k === 'zoom' || k === 'ir' || k === 'pano') && <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: typFarbe({ typ: k } as Foto) }} />}
      <span className="font-semibold tabular-nums text-dark-text">{zaehler[k]}</span><span>{text}</span></button>)
  const feld = 'min-h-[44px] w-full rounded-lg border border-dark-border bg-dark-bg-secondary px-3 text-sm text-dark-text'
  const leer = gruppen && !gruppen.einsaetze.length && gruppen.ohne_einsatz.fotos === 0

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-dark-text-muted">Fotos aus DJI Pilot 2 je Einsatz; Originale werden nach der Aufbewahrungsfrist gelöscht, die verkleinerte Fassung bleibt.</p>
      </div>

      {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{fehler}</div>}
      {leer && <p className="rounded-xl border border-dark-border p-6 text-center text-sm text-dark-text-muted">Noch keine Fotos hochgeladen.</p>}

      {gruppen && !leer && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
            <label className="block text-xs text-dark-text-muted">Einsatz
              <select className={`${feld} mt-1`} value={auswahl ?? ''} onChange={e => { setParams(p => { const n = new URLSearchParams(p); n.set('gruppe', e.target.value); n.delete('flug'); return n }, { replace: true }) }}>
                {gruppen.einsaetze.map(e => (
                  <option key={e.id} value={e.id}>{datum(e.datum)} · Nr. {e.id} · {typLabel(e.typ ?? 'EINSATZ')} · {e.titel} · {e.fotos} Foto{e.fotos === 1 ? '' : 's'}{e.status ? ` · ${statusLabel(e.status)}` : ''}</option>
                ))}
                {gruppen.ohne_einsatz.fotos > 0 && <option value="ohne">Ohne Einsatz · {gruppen.ohne_einsatz.fotos} Fotos</option>}
              </select>
            </label>
            <label className="block text-xs text-dark-text-muted">Flug
              <select className={`${feld} mt-1`} value={flug} onChange={e => setParam('flug', e.target.value || null)} disabled={!fotos}>
                <option value="">Alle Flüge</option>
                {fluege.map(([id, t]) => <option key={id} value={id}>{t}</option>)}
                {(fotos ?? []).some(f => f.flug_id == null) && <option value="ohne">Ohne Flug</option>}
              </select>
            </label>
            <div className="flex items-end gap-2">
              <button type="button" onClick={() => { void gruppenLaden(); void fotosLaden() }} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-dark-border px-3 text-xs text-dark-text-muted"><RefreshCw className="h-3.5 w-3.5" />Aktualisieren</button>
              <button type="button" onClick={zipLaden} disabled={!sichtbar.length || !!zipLaeuft}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-primary-500 bg-primary-500/15 px-4 text-sm font-semibold text-dark-text disabled:opacity-40">
                {zipLaeuft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{zipLaeuft ?? `Alle als ZIP (${sichtbar.length})`}</button>
            </div>
          </div>
          {gruppe && <p className="text-xs text-dark-text-muted">Einsatz {gruppe.id}: {gruppe.titel} · {datum(gruppe.datum)} · Status {gruppe.status ? statusLabel(gruppe.status) : '–'} · {gruppe.fotos} Fotos, davon {gruppe.ir} IR, {gruppe.hotspots} Hotspots</p>}
          {hinweis && <p role="status" className="text-xs text-amber-400">{hinweis}</p>}
          {kachelFehler && <p role="alert" className="text-xs text-red-300">{kachelFehler}</p>}

          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Fototyp filtern">
            {chip('alle', 'Alle')}{chip('weit', 'Weitwinkel')}{chip('zoom', 'Zoom')}{chip('ir', 'IR')}{chip('pano', 'Panorama')}{chip('hotspot', 'Nur mit Hotspot')}{chip('bericht', 'Für den Bericht')}
            <button type="button" aria-pressed={kartePlatz} onClick={() => setKartePlatz(v => !v)} data-testid="karte-umschalten"
              className="ml-auto inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-dark-border px-3.5 text-sm text-dark-text-muted hover:text-dark-text">
              {kartePlatz ? 'Karte einklappen' : 'Karte einblenden'}</button>
          </div>

          {fotos === null ? <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-dark-text-muted" /></div>
            : sichtbar.length === 0 ? <p className="rounded-xl border border-dark-border p-6 text-center text-sm text-dark-text-muted">Keine Fotos in dieser Auswahl.</p>
            : (
              <div className={`grid gap-4 ${kartePlatz ? 'lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : ''}`}>
                <div className="order-2 lg:order-1">
                  <ul className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${kartePlatz ? 'xl:grid-cols-4' : 'lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'}`}>
                    {sichtbar.map((f, i) => (
                      <li key={f.id} className="relative">
                        <button type="button" ref={el => { kacheln.current[f.id] = el }} onClick={() => { setGewaehlt(f.id); setOffen(i) }} onMouseEnter={() => setGewaehlt(f.id)}
                          aria-label={`${TYP_TEXT[f.typ ?? 'weit']} ${zeit(aufnahme(f))} öffnen`}
                          className={`group relative block w-full overflow-hidden rounded-xl border-2 text-left ${gewaehlt === f.id ? 'border-yellow-400' : 'border-dark-border'}`}>
                          <Vorschau foto={f} />
                          <span className="medien-typ-chip absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" data-typ={f.typ ?? "weit"} style={{ background: typFarbeUi(f) }}>{TYP_TEXT[f.typ ?? 'weit']}</span>
                          {f.hotspots.length > 0 && <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white"><Flame className="h-3 w-3" />{f.hotspots[0].temp_max.toFixed(0)} °C</span>}
                          <span className="block bg-dark-bg-secondary px-2 py-1 text-[11px] text-dark-text-muted">{zeit(aufnahme(f))}{!f.hat_original && ' · ohne Original'}</span>
                        </button>
                        {f.bericht_markiert && <span data-testid="bericht-kennzeichen" className="pointer-events-none absolute left-1.5 top-8 inline-flex items-center gap-0.5 rounded-full bg-green-600 px-2 py-0.5 text-[11px] font-semibold text-white"><FileText className="h-3 w-3" />Für Bericht</span>}
                        <button type="button" data-testid="kachel-bericht" aria-pressed={!!f.bericht_markiert}
                          aria-label={f.bericht_markiert ? 'Markierung für den Bericht entfernen' : 'Für den Bericht markieren'}
                          title={f.bericht_markiert ? 'Markierung für den Bericht entfernen' : 'Für den Bericht markieren'}
                          onClick={async () => setKachelFehler(await markieren(f) ?? '')}
                          className={`absolute bottom-9 right-1.5 flex h-11 w-11 items-center justify-center rounded-full border-2 ${f.bericht_markiert ? 'border-green-400 bg-green-600 text-white' : 'border-white/70 bg-black/60 text-white'}`}><FileText className="h-5 w-5" /></button>
                      </li>
                    ))}
                  </ul>
                </div>
                {kartePlatz && <div className="order-1 h-[280px] lg:sticky lg:top-2 lg:order-2 lg:h-[calc(100dvh-14rem)] lg:min-h-[320px] lg:self-start">
                  <Karte fotos={sichtbar} gewaehlt={gewaehlt} onWahl={waehlen} />
                </div>}
              </div>
            )}
        </>
      )}

      {offen != null && sichtbar[offen] && (
        <Grossansicht fotos={sichtbar} index={offen} onMarkieren={markieren} onEinordnen={einordnen} onIndex={i => { setOffen(i); setGewaehlt(sichtbar[i].id) }}
          onClose={() => { const id = sichtbar[offen]?.id; setOffen(null); if (id != null) setTimeout(() => kacheln.current[id]?.focus({ preventScroll: true }), 0); if (id != null) waehlen(id) }} />
      )}
    </div>
  )
}
